/**
 * Date-range maths for the legacy picker. Pure and timezone-naive on purpose:
 * every value is a LOCAL calendar day, because the filters these feed are
 * `YYYY-MM-DD` strings the backend interprets as calendar days, not instants.
 * Doing this in UTC would shift the boundary for anyone east of Greenwich —
 * "today" would start at 05:00 for a Tashkent merchant.
 */

/** A closed range of calendar days. `from` and `to` may be the same day. */
export interface LegacyDateRange {
  from: Date;
  to: Date;
}

/** The preset ranges offered beside the calendar, in the old panel's order. */
export type LegacyRangePresetId = 'yesterday' | 'today' | 'week' | 'month' | 'year';

export const LEGACY_RANGE_PRESETS: readonly LegacyRangePresetId[] = [
  'yesterday',
  'today',
  'week',
  'month',
  'year',
] as const;

/** Midnight on the same calendar day, in local time. */
export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Start of the week containing `date` — MONDAY-based.
 *
 * Verified against the live panel, which renders "This Week: 03.08.2026 -
 * 05.08.2026" on Wednesday 5 Aug 2026; 3 Aug is the Monday. dayjs defaults to
 * Sunday, so the old app is running a Monday-first locale — assuming the
 * library default here silently shifts every "this week" filter back a day.
 */
export function startOfWeek(date: Date): Date {
  const d = startOfDay(date);
  // `getDay()` is Sunday-based (0=Sun), so Sunday must step back six days to
  // reach its Monday, not zero.
  const offset = (d.getDay() + 6) % 7;
  return addDays(d, -offset);
}

export function startOfMonth(date: Date): Date {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
}

export function startOfYear(date: Date): Date {
  const d = startOfDay(date);
  d.setMonth(0, 1);
  return d;
}

/** Resolves a preset to its concrete range, relative to `today`. */
export function resolvePreset(
  preset: LegacyRangePresetId,
  today: Date = new Date(),
): LegacyDateRange {
  const base = startOfDay(today);

  switch (preset) {
    case 'yesterday': {
      const y = addDays(base, -1);
      return { from: y, to: y };
    }
    case 'today':
      return { from: base, to: base };
    case 'week':
      return { from: startOfWeek(base), to: base };
    case 'month':
      return { from: startOfMonth(base), to: base };
    case 'year':
      return { from: startOfYear(base), to: base };
  }
}

/** True when two dates fall on the same calendar day. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Which preset (if any) a range currently matches — drives the active card. */
export function matchPreset(
  range: LegacyDateRange,
  today: Date = new Date(),
): LegacyRangePresetId | null {
  return (
    LEGACY_RANGE_PRESETS.find((preset) => {
      const candidate = resolvePreset(preset, today);
      return isSameDay(candidate.from, range.from) && isSameDay(candidate.to, range.to);
    }) ?? null
  );
}

/** `DD.MM.YYYY` — how the picker and its trigger label render a day. */
export function formatDisplayDate(date: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${p(date.getDate())}.${p(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/** `YYYY-MM-DD` — the wire format every legacy list endpoint expects. */
export function formatWireDate(date: Date): string {
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/** Parses `YYYY-MM-DD` back to a local calendar day; null if malformed. */
export function parseWireDate(value: string | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  // Built from parts rather than `new Date(string)`, which parses a bare
  // `YYYY-MM-DD` as UTC midnight and lands on the previous day in the West.
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(d.getTime()) ? null : startOfDay(d);
}

/**
 * The six-week grid the calendar renders for a month: 42 cells, Monday-first,
 * padded with the neighbouring months' days (which render greyed).
 *
 * Always 42 cells so the panel never changes height between months — the old
 * calendar was a fixed 389px box.
 */
const CALENDAR_CELLS = 42;

export function buildMonthGrid(month: Date): Date[] {
  const first = startOfMonth(month);
  // `getDay()` is Sunday-based; the GRID is Monday-first, so Sunday (0) sits
  // in the last column and needs six leading blanks, not none.
  const leading = (first.getDay() + 6) % 7;
  const start = addDays(first, -leading);
  return Array.from({ length: CALENDAR_CELLS }, (_, i) => addDays(start, i));
}
